import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useBoolVariation } from "@launchdarkly/react-sdk";
import { hasGlobalPrivacyControl, shouldEnableAnalyticsByDefault } from "./gpc";
import { siteUrl } from "./site";

interface CookieConsentContextValue {
  /**
   * Whether the "analytics" cookie category is currently accepted. Defaults
   * to `true` (today's unconditional-analytics behavior) when the cookie
   * banner feature flag is off, or before it has resolved.
   */
  analyticsAccepted: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue>({
  analyticsAccepted: true,
});

/** Whether the "analytics" cookie category is currently accepted. */
export function useAnalyticsConsent(): boolean {
  return useContext(CookieConsentContext).analyticsAccepted;
}

interface CookieConsentProviderProps {
  children: ReactNode;
}

/**
 * Initializes the `vanilla-cookieconsent` CCPA-style ("opt-out") banner when
 * the `enable-website-cookie-banner` LaunchDarkly flag is on, and exposes the
 * resulting analytics consent state to the rest of the app via
 * `useAnalyticsConsent()`.
 *
 * This is a "notice and opt-out" pattern, not a blocking GDPR-style consent
 * gate: analytics runs by default (unless GPC opts the visitor out), and the
 * preferences modal never auto-shows. The only UI is a small, persistent
 * "Your Privacy Choices" icon fixed to the bottom-right corner of the page,
 * which opens the preferences modal on click - the vanilla-cookieconsent
 * modal/banner markup itself is otherwise plain DOM managed entirely by the
 * library (appended to `document.body`). `CookieConsent.run()` guards itself
 * against being run more than once per page (via an internal `window._ccRun`
 * flag), so it's safe to call from this effect even under React StrictMode's
 * dev-only double-invocation.
 */
export function CookieConsentProvider({
  children,
}: CookieConsentProviderProps) {
  const bannerEnabled = useBoolVariation("enable-website-cookie-banner", false);
  const [analyticsAccepted, setAnalyticsAccepted] = useState(true);

  useEffect(() => {
    if (!bannerEnabled) return;

    (async () => {
      const [CookieConsent] = await Promise.all([
        import("vanilla-cookieconsent"),
        import("vanilla-cookieconsent/dist/cookieconsent.css"),
      ]);

      const analyticsEnabledDefault = shouldEnableAnalyticsByDefault(
        hasGlobalPrivacyControl(),
      );

      const syncAnalyticsConsent = () => {
        setAnalyticsAccepted(CookieConsent.acceptedCategory("analytics"));
      };

      await CookieConsent.run({
        mode: "opt-out",
        // Notice-and-opt-out pattern: never pop up the modal on its own -
        // the visitor discovers/opens it via the persistent bottom-right
        // icon (or the "Manage Cookie Preferences" links elsewhere in the
        // app) instead of being interrupted on page load.
        autoShow: false,
        categories: {
          necessary: {
            readOnly: true,
            enabled: true,
          },
          analytics: {
            enabled: analyticsEnabledDefault,
          },
        },
        onConsent: syncAnalyticsConsent,
        onChange: syncAnalyticsConsent,
        language: {
          default: "en",
          translations: {
            en: {
              consentModal: {
                title: "Your Privacy Choices",
                description:
                  `We use cookies from third-party analytics and advertising providers. ` +
                  `California residents have the right to opt out of the sale/sharing ` +
                  `of personal information. See our ` +
                  `<a href="${siteUrl("/privacy/")}" target="_blank" rel="noreferrer">Privacy Policy</a> ` +
                  `for details.`,
                acceptAllBtn: "OK",
                showPreferencesBtn: "Do Not Share My Personal Information",
              },
              preferencesModal: {
                title: "Cookie Preferences",
                acceptAllBtn: "Accept all",
                acceptNecessaryBtn: "Do Not Share My Personal Information",
                savePreferencesBtn: "Save preferences",
                closeIconLabel: "Close",
                sections: [
                  {
                    title: "Strictly Necessary",
                    description:
                      "Used to operate the site and its features, and to understand " +
                      "product usage so we can improve IrisChat. This information is " +
                      "used only by us for our own purposes, is not sold or shared, " +
                      "and cannot be disabled here.",
                    linkedCategory: "necessary",
                  },
                  {
                    title: "Analytics & Advertising",
                    description:
                      "Used by third-party analytics and advertising providers, who " +
                      "may use this information for their own purposes (such as " +
                      "measuring ad performance or improving their own services). " +
                      "Opting out here limits this kind of sharing.",
                    linkedCategory: "analytics",
                  },
                ],
              },
            },
          },
        },
      });

      syncAnalyticsConsent();
    })();
  }, [bannerEnabled]);

  return (
    <CookieConsentContext.Provider value={{ analyticsAccepted }}>
      {children}
      {bannerEnabled && <PrivacyChoicesFab />}
    </CookieConsentContext.Provider>
  );
}

/**
 * Small, persistent "Privacy Choices" link fixed to the bottom-right corner
 * of the viewport. Non-intrusive alternative to auto-popping the consent
 * modal - opens the preferences modal on click via the library's `data-cc`
 * auto-wiring. Renders as a plain underlined text link (not a button chip),
 * with the icon accompanying the required text per CCPA/CPRA's opt-out icon
 * rules (the icon may accompany the text link, not replace it).
 */
function PrivacyChoicesFab() {
  return (
    <button
      type="button"
      data-cc="show-preferencesModal"
      className="cookie-preferences-fab"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9.5" />
        <circle cx="9" cy="9.5" r="1" fill="currentColor" stroke="none" />
        <circle cx="13.5" cy="8" r="1" fill="currentColor" stroke="none" />
        <circle cx="15" cy="12.5" r="1" fill="currentColor" stroke="none" />
        <circle cx="9.5" cy="14" r="1" fill="currentColor" stroke="none" />
        <circle cx="13" cy="16" r="1" fill="currentColor" stroke="none" />
      </svg>
      <span className="cookie-preferences-fab-label">Privacy Choices</span>
    </button>
  );
}
